import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
} from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import NotificationSettings from "./NotificationSettings";

interface NotificationSettingsPageProps {
  userId: string;
}

export default function NotificationSettingsPage({
  userId,
}: NotificationSettingsPageProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setSuccessMessage("Settings updated successfully!");
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Notification Settings
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 600px" }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <SettingsIcon
                sx={{ mr: 2, fontSize: 40, color: "primary.main" }}
              />
              <Box>
                <Typography variant="h5" gutterBottom>
                  Manage Your Notifications
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Configure how and when you receive notifications about your
                  trading activities and events.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Card sx={{ flex: "1 1 300px" }}>
                <CardHeader title="Event Notifications" />
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Get notified before important events occur. Configure timing
                    for each notification channel.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setSettingsOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Configure Event Notifications
                  </Button>
                </CardContent>
              </Card>

              <Card sx={{ flex: "1 1 300px" }}>
                <CardHeader title="Trade Notifications" />
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Receive notifications when trades are created, updated, or
                    when positions are opened/closed.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setSettingsOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Configure Trade Notifications
                  </Button>
                </CardContent>
              </Card>

              <Card sx={{ flex: "1 1 300px" }}>
                <CardHeader title="Wallet Alerts" />
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Get alerts for wallet balance changes, deposits,
                    withdrawals, and other wallet activities.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setSettingsOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Configure Wallet Alerts
                  </Button>
                </CardContent>
              </Card>

              <Card sx={{ flex: "1 1 300px" }}>
                <CardHeader title="Quiet Hours" />
                <CardContent>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Set times when you don't want to receive notifications.
                    Perfect for sleeping hours.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setSettingsOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Configure Quiet Hours
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: "0 1 300px" }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => setSettingsOpen(true)}
                startIcon={<SettingsIcon />}
              >
                Open All Settings
              </Button>
              <Button variant="outlined">Test Notifications</Button>
              <Button variant="outlined">View Notification History</Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <NotificationSettings
        open={settingsOpen}
        onClose={handleSettingsClose}
        userId={userId}
      />
    </Box>
  );
}
